import {
  type CertificateProgramProduct,
  type CertificateSubscriptionOffer,
} from '@mc/api'
import { hasDiscount } from 'pages/certificates/annualpass/data/subscriptionData'

export type CmHeroPrice = {
  /** The effective price to feature — discounted when a coupon applies, else list price. */
  finalPrice?: string
  /**
   * The list price to render struck through (without the billing suffix) — set
   * only when a discount applies.
   */
  originalPrice?: string
}

export type CmHeroPrices = {
  individualPrice: CmHeroPrice
  subscriptionPrice: CmHeroPrice
}

// Featured price + struck-through list price (only when discounted), from the
// pre-rounded net/list strings. `suffix` (e.g. `/yr`) applies to the featured
// price only. hasDiscount suppresses the strikethrough when net === list.
const buildHeroPrice = (
  netPrice: string | undefined,
  listPrice: string | undefined,
  suffix = '',
): CmHeroPrice => {
  const finalPrice = netPrice ?? listPrice
  if (!finalPrice) return {}

  return {
    finalPrice: `${finalPrice}${suffix}`,
    originalPrice: hasDiscount(netPrice, listPrice) ? listPrice : undefined,
  }
}

/**
 * Display prices for the V1/V2 hero selector, each sourced from the surface that
 * carries its own promo: the individual (this cert) price comes from the product
 * endpoint (cert promo `mccerts-promo-${slug}`), the subscription price from the
 * FCC offer (subscription promo `mccerts-promo-subscription`).
 * Once we productize the discount_slugs property, we can source the individual price from the offer directly.
 */
export const getCmHeroPrices = (
  product: CertificateProgramProduct | undefined,
  offer: CertificateSubscriptionOffer | null | undefined,
): CmHeroPrices => {
  const subscriptionPricing = offer?.subscriptionPlan?.pricing

  return {
    individualPrice: buildHeroPrice(
      product?.displayNetPriceRounded,
      product?.displayPriceRounded,
    ),
    subscriptionPrice: buildHeroPrice(
      subscriptionPricing?.formattedNetPriceRounded,
      subscriptionPricing?.formattedPriceRounded,
      subscriptionPricing?.formattedBillingSuffix ?? '',
    ),
  }
}
